import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { DELETE_REASON_CHOICES } from "@/components/DeleteRecordButton";

// ---------------------------------------------------------------------------
// InlineDeleteButton — compact Super Admin delete affordance for row actions
// on list, desk, and registry pages.
//
// Same contract as DeleteRecordButton, but rendered as a small icon-button
// (with an optional label) suitable for table/row cells:
//   * Mandatory reason dropdown (6 canonical choices).
//   * "Other" requires ≥3-char explanation.
//   * Typed-DELETE confirmation only when `confirmToken` prop is supplied.
//   * `isSuperAdmin` prop adds `?force=true` to the request so the backend
//     hard-delete guards can be bypassed for Super Admin.
//   * On success: shows a toast and invokes `onDeleted(id)` so the caller
//     can filter the row out of the local list immediately.
// ---------------------------------------------------------------------------

export function InlineDeleteButton({
  endpoint,                // e.g. `/pro-directory/${id}`
  recordLabel,             // "PRO entry", "Note", "Batch", "Track", …
  recordId,                // opaque id passed back to onDeleted
  confirmToken,            // optional typed-token to unlock (dependencies)
  disabled,
  disabledReason,
  isSuperAdmin,
  buttonSize = "sm",
  showLabel = false,
  variant = "outline",
  buttonClassName = "",
  onDeleted,               // (deletedId) => void
  testid,
  cascadePreview,          // optional {label,count}[]
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const needsToken = !!confirmToken;
  const typedOk = useMemo(() => {
    if (!needsToken) return true;
    return (typed || "").trim().toLowerCase() === confirmToken.trim().toLowerCase();
  }, [needsToken, typed, confirmToken]);
  const reasonOk = useMemo(() => {
    if (!reason) return false;
    if (reason === "Other") return (otherText || "").trim().length >= 3;
    return true;
  }, [reason, otherText]);
  const canProceed = !disabled && typedOk && reasonOk;

  function resetState() {
    setTyped("");
    setReason("");
    setOtherText("");
  }

  async function handleConfirm() {
    if (!canProceed || submitting) return;
    setSubmitting(true);
    try {
      const finalReason = reason === "Other" ? `Other: ${otherText.trim()}` : reason;
      const qs = new URLSearchParams();
      qs.set("reason", finalReason);
      if (isSuperAdmin) qs.set("force", "true");
      await api.delete(`${endpoint}?${qs.toString()}`);
      toast.success(`${recordLabel} deleted`);
      setOpen(false);
      resetState();
      onDeleted?.(recordId);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg;
      if (typeof raw === "string") msg = raw;
      else if (raw && typeof raw === "object") msg = raw.message || JSON.stringify(raw);
      else msg = e?.message || "Delete failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const trigger = (
    <Button
      variant={variant}
      size={buttonSize}
      disabled={disabled || submitting}
      onClick={(e) => { e.stopPropagation?.(); setOpen(true); }}
      className={buttonClassName + " text-[hsl(0_70%_38%)] hover:bg-[hsl(0_70%_95%)] hover:text-[hsl(0_70%_32%)]"}
      title={disabled ? (disabledReason || "Delete disabled") : `Delete ${recordLabel.toLowerCase()}`}
      data-testid={testid ? `${testid}-open` : undefined}
    >
      <Trash2 className={showLabel ? "w-3.5 h-3.5 mr-1.5" : "w-3.5 h-3.5"} />
      {showLabel ? `Delete ${recordLabel}` : null}
    </Button>
  );

  return (
    <>
      {trigger}
      <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetState(); }}>
        <AlertDialogContent data-testid={testid ? `${testid}-dialog` : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {recordLabel.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the {recordLabel.toLowerCase()} from active
              views. A minimal audit tombstone (who, when, type, ID, reason) is
              retained. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {Array.isArray(cascadePreview) && cascadePreview.length > 0 && (
            <div className="rounded-md border bg-secondary/50 p-2 text-[11.5px] leading-relaxed">
              <div className="font-medium mb-1">The following will also be removed:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {cascadePreview.map((row, i) => (
                  <li key={i}>{row.label}{typeof row.count === "number" ? ` (${row.count})` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-3 py-2">
            {needsToken && (
              <div>
                <Label className="text-[11.5px]">
                  Type <span className="font-mono px-1 py-0.5 rounded bg-secondary">{confirmToken}</span> to confirm
                </Label>
                <Input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoFocus
                  className="mt-1 font-mono"
                  placeholder={confirmToken}
                  data-testid={testid ? `${testid}-confirm-input` : undefined}
                />
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Reason (required · captured in audit)
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1 text-[12.5px]" data-testid={testid ? `${testid}-reason-trigger` : undefined}>
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {DELETE_REASON_CHOICES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reason === "Other" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Explanation (required when reason is Other)
                </Label>
                <Textarea
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  rows={2}
                  placeholder="Briefly explain why this record is being deleted…"
                  className="mt-1 text-[12.5px]"
                  data-testid={testid ? `${testid}-reason-other` : undefined}
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={testid ? `${testid}-cancel` : undefined}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canProceed || submitting}
              onClick={(e) => { e.preventDefault(); handleConfirm(); }}
              className="bg-[hsl(0_70%_42%)] hover:bg-[hsl(0_70%_38%)] text-white"
              data-testid={testid ? `${testid}-confirm` : undefined}
            >
              {submitting ? "Deleting…" : `Delete ${recordLabel}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default InlineDeleteButton;
